import * as v from "valibot";
import { ArticleIdeaStatus, ArticleStatus } from "../../models/article";
import { articleDto } from "../articles.dto";
import { pageRequest, pageResponse } from "../paging.dto";
import { aliasField, articleKey, imageDto, levelDto } from "../shared.dto";
import { adminIdeaDto } from "./ideas.dto";

export const ARTICLE_STATUSES: ArticleStatus[] = Object.values(ArticleStatus);

export const articleStatusDto = v.picklist(ARTICLE_STATUSES);

/** An article's listing row as the admin sees it, in every status. */
export const adminArticleMetaDto = v.object({
  authorAlias: v.string(),
  id: v.string(),
  ideaId: v.string(),
  path: v.string(),
  image: v.optional(imageDto),
  title: v.string(),
  tagline: v.string(),
  category: v.string(),
  subcategory: v.string(),
  subjects: v.array(v.string()),
  level: levelDto,
  tags: v.array(v.string()),
  wordCount: v.number(),
  readingMinutes: v.number(),
  aiModel: v.string(),
  createdAt: v.number(),
  updatedAt: v.number(),
  publishedAt: v.number(),
  status: articleStatusDto,
});
export type AdminArticleMeta = v.InferOutput<typeof adminArticleMetaDto>;

const filterText = v.pipe(v.string(), v.maxLength(64));

/** `keyword` matches the title, tagline and tags of the page it reads, so it narrows a page rather than the whole set. */
export const listAdminArticlesRequest = {
  status: v.optional(articleStatusDto),
  category: v.optional(filterText),
  author: v.optional(filterText),
  keyword: v.optional(filterText),
  ...pageRequest,
};

export const listAdminArticlesResponse = {
  articles: v.array(adminArticleMetaDto),
  ...pageResponse,
};

export const getAdminArticleRequest = articleKey;

export const getAdminArticleResponse = {
  meta: adminArticleMetaDto,
  article: articleDto,
  idea: v.nullable(adminIdeaDto),
};

const paragraphs = v.pipe(v.array(v.pipe(v.string(), v.minLength(1), v.maxLength(2000))), v.minLength(1), v.maxLength(20));

const keyPointUpdateDto = v.object({
  title: v.pipe(v.string(), v.minLength(1), v.maxLength(300)),
  paragraphs,
  takeaway: v.pipe(v.string(), v.minLength(1), v.maxLength(2000)),
});

export const updateAdminArticleRequest = {
  ...articleKey,
  meta: v.object({
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(300)),
    tagline: v.pipe(v.string(), v.minLength(1), v.maxLength(1000)),
    tags: v.pipe(v.array(v.pipe(v.string(), v.regex(/^[a-z0-9-]+$/), v.maxLength(32))), v.maxLength(5)),
  }),
  article: v.object({
    summary: paragraphs,
    introduction: paragraphs,
    keyPoints: v.pipe(v.array(keyPointUpdateDto), v.minLength(1), v.maxLength(20)),
    conclusion: paragraphs,
    metaDescription: v.pipe(v.string(), v.minLength(1), v.maxLength(256)),
    searchKeywords: v.pipe(v.array(v.pipe(v.string(), v.minLength(1), v.maxLength(64))), v.maxLength(32)),
  }),
};

/** The moves an admin may make by hand: park an article for audio or publishing, or archive it. Unarchive is a move to `pending_audio`. */
export const ADMIN_ARTICLE_STATUS_MOVES: ArticleStatus[] = [ArticleStatus.PENDING_AUDIO, ArticleStatus.PENDING_PUBLISH, ArticleStatus.ARCHIVED];

export const setAdminArticleStatusRequest = {
  ...articleKey,
  status: v.picklist(ADMIN_ARTICLE_STATUS_MOVES),
};

export const deleteAdminArticleResponse = { ideaStatus: v.nullable(v.picklist(Object.values(ArticleIdeaStatus))) };

export const publishAdminArticlesRequest = {
  articles: v.pipe(v.array(v.object({ alias: aliasField, articleId: aliasField })), v.minLength(1), v.maxLength(20)),
};

export const publishAdminArticlesResponse = {
  published: v.number(),
  failed: v.number(),
};
